import UnfoldLessIcon from "@mui/icons-material/UnfoldLess";
import { Box, IconButton, Typography } from "@mui/material";
import type { FC } from "react";
import { useHospitalData } from "@/contexts/HospitalDataProvider";
import { useJobColors } from "@/hooks/useJobColors";
import { useRoleGroupColors } from "@/hooks/useRoleColors";
import { getSectionTone, TOTAL_WIDTH } from "./consts";
import type { RoleSection } from "./useWeeklyGapView";

const NO_ROLE_GROUPS: Record<string, string[]> = {};

export const RoleSectionHeader: FC<{
	section: RoleSection;
	roleGroups?: Record<string, string[]>;
	hasExpanded: boolean;
	onCollapseAll: () => void;
}> = ({ section, roleGroups, hasExpanded, onCollapseAll }) => {
	const { shortRole } = section;
	// Demo styling only for hospital demos (Hospital type + inhouse2).
	const { isHospitalView: isHospitalDemoView } = useHospitalData();
	const jobColor = useJobColors();
	const roleGroupColor = useRoleGroupColors();
	// DEFAULT: a view grouped by role (not role group) passes no groups; a role code resolves without them.
	const tone = getSectionTone({ ...section, isJobSection: section.isJobSection === true }, roleGroups ?? NO_ROLE_GROUPS, jobColor, roleGroupColor);
	// Demo shows a brand-colored header; others keep the role/job color. Both the band and its text
	// track the department's Appearance setting (primary.dark / contrastText) instead of a fixed green.
	const bg = isHospitalDemoView ? "primary.dark" : tone.band;
	const text = isHospitalDemoView ? "primary.contrastText" : tone.text;
	return (
		<Box sx={{ width: TOTAL_WIDTH, bgcolor: "white" }}>
			<Box
				sx={{
					position: "relative",
					bgcolor: bg,
					px: 2,
					py: 0.5,
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
				}}
			>
				<Typography data-testid={`weekly-role-header-label-${shortRole}`} sx={{ fontSize: "14px", fontWeight: 700, color: text, lineHeight: "14px" }}>
					{shortRole}
				</Typography>
				{hasExpanded && (
					<IconButton
						data-testid={`weekly-role-collapse-btn-${shortRole}`}
						size="small"
						onClick={(e) => {
							e.stopPropagation();
							onCollapseAll();
						}}
						sx={{ p: 0.25, opacity: 0.8, "&:hover": { opacity: 1 }, position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)" }}
					>
						<UnfoldLessIcon sx={{ fontSize: 14 }} />
					</IconButton>
				)}
			</Box>
		</Box>
	);
};
